import styled, { css } from "styled-components";
import { controlSize } from "../../../design-system/tokens/controlSizes";
import { borderRadius, borderWidth, fontWeights, spacing, typographyMixin } from "@magicbi/ui-library";
import type { FilterChipDot, FilterChipSelectedLook, FilterChipSize } from "./FilterChipGroup.types";

export const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]};
`;

export const ChipButton = styled.button<{ $pressed: boolean; $size: FilterChipSize; $selectedLook: FilterChipSelectedLook }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  height: ${spacing[8]};
  padding: 0 ${spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${borderRadius.full};
  background: ${({ theme }) => theme.colors.background.primary};
  ${typographyMixin("body", 4)}
  font-weight: ${fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
  }
  ${({ $size }) =>
    $size === "sm" &&
    css`
      height: ${controlSize.pill.sm};
      ${typographyMixin("caption", 2)}
    `}
  ${({ $pressed, $selectedLook, $size }) =>
    $pressed &&
    $selectedLook === "tinted" &&
    css`
      border-color: ${({ theme }) => ($size === "sm" ? theme.colors.primary[100] : theme.colors.interactive.primary)};
      background: ${({ theme }) => ($size === "sm" ? theme.colors.primary[100] : theme.colors.background.tertiary)};
      color: ${({ theme }) => theme.colors.text.link};
      &:hover {
        background: ${({ theme }) => ($size === "sm" ? theme.colors.primary[100] : theme.colors.background.tertiary)};
      }
    `}
  ${({ $pressed, $selectedLook }) =>
    $pressed &&
    $selectedLook === "solid" &&
    css`
      border-color: ${({ theme }) => theme.colors.background.inverse};
      background: ${({ theme }) => theme.colors.background.inverse};
      color: ${({ theme }) => theme.colors.text.inverse};
      font-weight: ${fontWeights.semibold};
      &:hover {
        background: ${({ theme }) => theme.colors.background.inverse};
      }
    `}
`;

export const Count = styled.span<{ $pressed: boolean; $size: FilterChipSize; $selectedLook: FilterChipSelectedLook }>`
  font-weight: ${fontWeights.regular};
  color: ${({ theme, $pressed, $selectedLook }) =>
    $pressed ? ($selectedLook === "solid" ? theme.colors.text.inverse : theme.colors.text.link) : theme.colors.text.secondary};
  ${({ $size }) =>
    $size === "sm" &&
    css`
      &::before {
        content: "\\00b7";
        margin-right: ${spacing[1]};
      }
    `}
`;

export const Dot = styled.i<{ $dot: FilterChipDot }>`
  flex: none;
  width: ${spacing[2]};
  height: ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${({ theme, $dot }) =>
    $dot === "success"
      ? theme.colors.success[500]
      : $dot === "error"
        ? theme.colors.error[500]
        : $dot === "warning"
          ? theme.colors.warning[500]
          : theme.colors.text.tertiary};
`;
